import React from "react";

import assign from "lodash/assign";
import every from "lodash/every";
import groupBy from "lodash/groupBy";
import map from "lodash/map";
import reduce from "lodash/reduce";
import moment from "moment";
import {
  injectIntl,
  FormattedMessage,
  defineMessages,
  IntlShape,
} from "react-intl";

import { Text } from "@/components/atoms/Text";
import { IOperatingHour } from "~/types/api-types";

import { TimeMap } from "./data/time-data";
import { TimeUtils } from "./data/time-utils";

interface ReducedOpHours {
  [key: string]: {
    day: { id: string; defaultMessage: string };
    operating_hours: Array<{ time_string: string }>;
  };
}

interface TimeIndex {
  time: string;
}

const reducedOpHours = (
  opHours: IOperatingHour[],
  intl: IntlShape,
): ReducedOpHours => {
  const timeText = defineMessages({
    all_day: {
      id: "listing.operating_hours.all_day",
      defaultMessage: "All day (24 hours)",
    },
    closed: {
      id: "listing.operating_hours.closed",
      defaultMessage: "Closed",
    },
    monday: {
      id: "listing.operating_hours.default_monday",
      defaultMessage: "Monday",
    },
    tuesday: {
      id: "listing.operating_hours.default_tuesday",
      defaultMessage: "Tuesday",
    },
    wednesday: {
      id: "listing.operating_hours.default_wednesday",
      defaultMessage: "Wednesday",
    },
    thursday: {
      id: "listing.operating_hours.default_thursday",
      defaultMessage: "Thursday",
    },
    friday: {
      id: "listing.operating_hours.default_friday",
      defaultMessage: "Friday",
    },
    saturday: {
      id: "listing.operating_hours.default_saturday",
      defaultMessage: "Saturday",
    },
    sunday: {
      id: "listing.operating_hours.default_sunday",
      defaultMessage: "Sunday",
    },
  });

  const defaultOpHours = {
    1: { day: timeText.monday, operating_hours: [] },
    2: { day: timeText.tuesday, operating_hours: [] },
    3: { day: timeText.wednesday, operating_hours: [] },
    4: { day: timeText.thursday, operating_hours: [] },
    5: { day: timeText.friday, operating_hours: [] },
    6: { day: timeText.saturday, operating_hours: [] },
    7: { day: timeText.sunday, operating_hours: [] },
  };

  const groupedOpHours = groupBy(opHours, "dow");

  return reduce(
    defaultOpHours,
    (result, value, key) => {
      let newOpHoursHash: ReducedOpHours[keyof ReducedOpHours];
      const ophourResult = result;
      if (groupedOpHours[key]) {
        const mappedOpHours = map(groupedOpHours[key], (ophour) => {
          const startTime = ophour.start?.time;
          const endTime = ophour.end?.time;
          let timeStringMap;

          if (ophour.all_day) {
            timeStringMap = {
              time_string: intl.formatMessage(timeText.all_day),
            };
          } else if (startTime && endTime) {
            const startTimeIndex: number | null =
              TimeUtils.findTimeIndex(startTime);
            const endTimeIndex: number | null = TimeUtils.findTimeIndex(
              endTime,
              true,
            );

            if (startTimeIndex !== null && endTimeIndex !== null) {
              const momentStartTime = moment(
                (TimeMap[startTimeIndex] as TimeIndex).time,
                "LT",
              ).format();
              const momentEndTime = moment(
                (TimeMap[endTimeIndex] as TimeIndex).time,
                "LT",
              ).format();

              timeStringMap = {
                time_string: `${intl.formatTime(momentStartTime)} - ${intl.formatTime(momentEndTime)}`,
              };
            } else {
              // Handle the case where startTimeIndex or endTimeIndex is null
              timeStringMap = {
                time_string: intl.formatMessage(timeText.closed),
              };
            }
          } else if (ophour.is_social_event_policy_all_day) {
            const defaultStartTime = moment(
              (TimeMap[0] as TimeIndex).time,
              "LT",
            ).format();
            const defaultEndTime = moment(
              (TimeMap[36] as TimeIndex).time,
              "LT",
            ).format();

            timeStringMap = {
              time_string: `${intl.formatTime(defaultStartTime)} - ${intl.formatTime(defaultEndTime)}`,
            };
          }

          return assign({}, ophour, timeStringMap);
        });
        newOpHoursHash = {
          ...value,
          day: {
            ...value.day,
            defaultMessage: value.day.defaultMessage ?? "",
          },
          operating_hours: mappedOpHours,
        };
      } else {
        newOpHoursHash = {
          ...value,
          day: {
            ...value.day,
            defaultMessage: value.day.defaultMessage ?? "",
          },
          operating_hours: [
            { time_string: intl.formatMessage(timeText.closed) },
          ],
        };
      }
      ophourResult[key] = newOpHoursHash;
      return ophourResult;
    },
    {} as ReducedOpHours,
  );
};

const AllDow24HoursComp: React.FC = () => (
  <div className="tw-flex tw-space-between">
    <div className="tw-flex-1">
      <FormattedMessage
        id="listing.operating_hours.all_days"
        defaultMessage="Monday - Sunday"
      />
    </div>
    <div className="tw-flex-1">
      <FormattedMessage
        id="listing.operating_hours.all_time"
        defaultMessage="All day (24 hours)"
      />
    </div>
  </div>
);

interface ListOpHoursProps {
  opHours: IOperatingHour[];
  intl: IntlShape;
}

const ListOpHours: React.FC<ListOpHoursProps> = ({ opHours, intl }) => {
  const mappedOpHours = map(reducedOpHours(opHours, intl), (value, i) => {
    const operatingHours = (value || {}).operating_hours;

    return (
      <div key={`op-hours-list=${i}`} className={`tw-flex tw-space-between`}>
        <div className="tw-flex-1">{intl.formatMessage(value.day)}</div>
        <div className="tw-flex-1">
          {operatingHours.map((val) => (
            <div key={`${value.day}-${val.time_string}`}>{val.time_string}</div>
          ))}
        </div>
      </div>
    );
  });

  return <div>{mappedOpHours}</div>;
};

interface OpHoursCompProps {
  opHours: IOperatingHour[];
  intl: IntlShape;
}

const OpHoursComp: React.FC<OpHoursCompProps> = ({
  opHours = [],
  intl,
  ...rest
}) => {
  const isAllDow24hours =
    every(opHours, { all_day: true }) && 7 === opHours.length;
  return (
    <div className="tw-my-40" id="ophours_section">
      <Text
        element="h2"
        typeface="headline-2-m"
        data-testing-id="operatingHoursSectionTitle"
      >
        <FormattedMessage
          id="listing.listing_main.operating_hours_header"
          defaultMessage="Bookable Hours"
        />
      </Text>
      {isAllDow24hours ? (
        <AllDow24HoursComp {...rest} />
      ) : (
        <ListOpHours intl={intl} opHours={opHours} {...rest} />
      )}
    </div>
  );
};

export default injectIntl(OpHoursComp);
